import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Grid from '@material-ui/core/Grid';
import CircularProgress from '@material-ui/core/CircularProgress';

import {
  kitSelector,
  patientSelector,
  procedureSelector,
  physicianSelector,
} from 'common/utils/entitySelectors';
import routes from 'common/constants/path';
import { formatPath } from 'common/utils/helpers/url';
import useAuthProvider from 'store/authProvider/useAuthProvider';

import { PatientInfo, ProcedureInfo, ShortInfo } from './components';
import FormatData from './formatData';
import useStyles from './styles';

const UserCard = forwardRef(
  (
    {
      isChildTenantProcedure,
      shortInfo = false,
      procedureId,
      tenantName,
      ...props
    },
    ref
  ) => {
    const classes = useStyles({ isChildTenantProcedure });
    const { isEcgTech } = useAuthProvider();

    const procedureEntity = useSelector(procedureSelector(procedureId));
    const kitEntity = useSelector(kitSelector(procedureEntity?.kit)) || {};
    const patientEntity =
      useSelector(patientSelector(procedureEntity?.patient)) || {};
    const physicianEntity =
      useSelector(physicianSelector(procedureEntity?.physician)) || {};

    if (!procedureEntity) {
      return (
        <Grid container justifyContent="center" alignItems="center">
          <CircularProgress />
        </Grid>
      );
    }

    const procedure = new FormatData(
      procedureEntity,
      patientEntity,
      physicianEntity,
      kitEntity
    );

    const path = formatPath(routes.private.procedures.monitoring.index, {
      id: procedureId,
    });

    return (
      <Grid
        {...props}
        container
        to={path}
        component={!isEcgTech && !isChildTenantProcedure ? Link : 'div'}
        direction="column"
        className={classes.link}
        ref={ref}
      >
        {shortInfo ? (
          <Grid item>
            <ShortInfo
              isChildTenantId={isChildTenantProcedure}
              procedureInfo={procedure.procedureInfo}
              tenantName={tenantName}
              userInfo={procedure.userInfo}
              kitInfo={procedure.kitInfo}
            />
          </Grid>
        ) : (
          <>
            <Grid item>
              <PatientInfo
                procedureInfo={procedure.procedureInfo}
                kitInfo={procedure.kitInfo}
                userInfo={procedure.userInfo}
              />
            </Grid>

            <Grid item>
              <ProcedureInfo
                procedureInfo={procedure.procedureInfo}
                physicianInfo={procedure.physicianInfo}
              />
            </Grid>
          </>
        )}
      </Grid>
    );
  }
);

UserCard.displayName = 'UserCard';

UserCard.propTypes = {
  shortInfo: PropTypes.bool,
  tenantName: PropTypes.string,
  procedureId: PropTypes.string,
  isChildTenantProcedure: PropTypes.bool,
};

export default UserCard;
